import { Link } from "wouter";
import { ArrowLeft, Check, Download, Image as ImageIcon, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { estadoDaConsulta } from "@/components/data/estado-consulta";
import { CompradorShell } from "@/components/mercado/produtor-shell";
import { StatusChip } from "@/components/mercado/status-chip";
import { usePode } from "@/lib/permissoes";
import { origemAtual, useNavegacao } from "@/lib/navegacao";
import { useToast } from "@/hooks/use-toast";
import {
  useCarga, useConfigDocumento, useMarcarDocumentoEnviado, useMensagemDaCarga,
} from "@/hooks/use-cargas";
import { fmtKgBR, fmtDataBR, fmtDiaCurto } from "@/lib/formato-mercado";
import type { CargaDetalheDTO } from "@shared/mercado-comprador";

/**
 * O DOCUMENTO da carga — a prévia fiel, e o envio ao cliente.
 *
 * ⚠️ SEM PREÇO NENHUM (decisão D4). Nesta fase a carga não tem dados de venda,
 * então o PDF, a imagem e o `/c/` levam o que o cliente confere na chegada: de
 * quem veio, quanto de cada classificação, peso bruto e líquido, placa e
 * motorista. O rodapé diz isso por extenso — sem a frase, a ausência de valores
 * é lida como documento incompleto, e alguém liga perguntando pelo total.
 *
 * ⚠️ A PRÉVIA É O MESMO LAYOUT DO PDF, em HTML. Não é uma segunda montagem: os
 * números vêm do MESMO `resumoDaCarga` que alimenta o gerador, e o que ela
 * desenha são as mesmas linhas. Uma prévia que some de outro lugar é uma prévia
 * que mente na primeira divergência.
 *
 * ⚠️ EM FORMAÇÃO é prévia e SÓ: PDF, imagem e envio ficam desabilitados, com a
 * razão escrita. O documento que envelhece a cada romaneio adicionado é pior que
 * nenhum, e o servidor recusa com 409 de qualquer jeito.
 */
export default function MercadoCargaDocumento({ id }: { id: string }) {
  const { voltar } = useNavegacao();
  const consulta = useCarga(id);
  const carga = consulta.data;

  const aguardando = estadoDaConsulta(consulta, { variante: "painel" });
  if (aguardando || !carga) return <CompradorShell>{aguardando}</CompradorShell>;

  const emFormacao = carga.status === "EM_FORMACAO";

  return (
    <CompradorShell className="space-y-4">
      <header className="space-y-2">
        <Button
          variant="ghost" size="sm" className="-ml-2"
          onClick={() => voltar(`/mercado/cargas/${carga.id}`)}
          data-testid="button-voltar"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />{carga.identificador}
        </Button>
        <div>
          <p className="text-xs text-muted-foreground">
            <Link href="/mercado/cargas" state={origemAtual()} className="hover:underline">Cargas</Link>
            {" / "}
            <Link href={`/mercado/cargas/${carga.id}`} state={origemAtual()} className="hover:underline font-mono">{carga.identificador}</Link>
            {" / Documento"}
          </p>
          <h1 className="text-2xl font-bold tracking-tight">Documento da carga</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {emFormacao
              ? "Prévia — o documento é liberado ao fechar a carga."
              : `PDF ou imagem para enviar a ${carga.clienteNome ?? "o cliente"}.`}
          </p>
        </div>
      </header>

      <div className="grid gap-4 lg:grid-cols-[1.3fr_1fr] lg:items-start">
        <Papel carga={carga} />
        <div className="space-y-4 min-w-0">
          <EnviarAoCliente carga={carga} />
          <OQueApareceNoDocumento carga={carga} />
        </div>
      </div>
    </CompradorShell>
  );
}

// ─── A prévia ────────────────────────────────────────────────────────────────

function Papel({ carga }: { carga: CargaDetalheDTO }) {
  const emFormacao = carga.status === "EM_FORMACAO";
  const colunas = carga.porClassificacao;
  const mostrarBruto = carga.documento.mostrarBruto && carga.resumo.kgBruto != null;

  return (
    <article
      // Fundo claro fixo mesmo no tema escuro: é papel, e o que ele vê aqui tem
      // de ser o que sai no PDF — uma prévia que muda de cor com o tema não
      // prevê nada.
      className="rounded-lg border bg-white text-[#121712] p-5 space-y-4 overflow-x-auto"
      data-testid="previa-documento"
    >
      <header className="flex items-start justify-between gap-4 border-b border-[#E1E7E1] pb-3">
        <div className="min-w-0">
          <p className="font-bold">{carga.clienteNome ? carga.identificador : carga.identificador}</p>
          <p className="text-[11.5px] text-[#4E574F]">Corretor de frutas</p>
        </div>
        <div className="shrink-0 text-right">
          <p className="text-[10px] uppercase tracking-wider text-[#4E574F]">Romaneio de carga</p>
          <p className="font-mono font-bold">{carga.identificador}</p>
          <p className="text-[10.5px] text-[#4E574F]">
            {emFormacao
              ? "prévia · não enviado"
              : carga.fechadaEm ? `emitido ${fmtDataBR(carga.fechadaEm.slice(0, 10))}` : "emitido"}
          </p>
        </div>
      </header>

      <dl className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-[12.5px]">
        <Campo rotulo="Cliente" valor={carga.clienteNome ?? "—"} />
        <Campo rotulo="Destino" valor={carga.clienteLocal ?? "—"} />
        <Campo rotulo="Carregamento" valor={carga.dataCarregamento ? fmtDiaCurto(carga.dataCarregamento) : "—"} />
        <Campo rotulo="Placa" valor={carga.placa ?? "—"} mono />
        <Campo rotulo="Motorista" valor={carga.motoristaNome ?? "—"} />
        <Campo
          rotulo="Produtores"
          valor={`${carga.resumo.nProdutores} · ${carga.resumo.nRomaneios} ${carga.resumo.nRomaneios === 1 ? "romaneio" : "romaneios"}`}
        />
      </dl>

      <table className="w-full text-[12.5px]">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-[#4E574F] border-b border-[#E1E7E1]">
            <th className="text-left font-semibold py-1.5">Produtor</th>
            {colunas.map(c => (
              <th key={c.chave} className="text-right font-semibold py-1.5 px-1.5 whitespace-nowrap">{c.classificacao}</th>
            ))}
            <th className="text-right font-semibold py-1.5 whitespace-nowrap">Total kg</th>
          </tr>
        </thead>
        <tbody>
          {/* ⚠️ Uma linha por PRODUTOR, e "Produtor N" na ordem da tabela quando
              o nome está escondido (D5) — o mesmo que o servidor faz no PDF. */}
          {linhasPorProdutor(carga).map((p, i) => (
            <tr key={p.contatoId} className="border-b border-[#F0F4F0]">
              <td className="py-1.5">
                {carga.documento.mostrarProdutores ? p.nome : `Produtor ${i + 1}`}
              </td>
              {colunas.map(c => (
                <td key={c.chave} className="py-1.5 px-1.5 text-right tabular-nums">
                  {p.porClassificacao[c.chave] ? fmtKgBR(p.porClassificacao[c.chave]) : "—"}
                </td>
              ))}
              <td className="py-1.5 text-right tabular-nums font-semibold">{fmtKgBR(p.totalKg)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="font-bold">
            <td className="py-1.5">Total líquido</td>
            {colunas.map(c => (
              <td key={c.chave} className="py-1.5 px-1.5 text-right tabular-nums">{fmtKgBR(c.kg)}</td>
            ))}
            <td className="py-1.5 text-right tabular-nums">{fmtKgBR(carga.resumo.kgLiquido)}</td>
          </tr>
        </tfoot>
      </table>

      {mostrarBruto && (
        <p className="text-[11.5px] text-[#4E574F] flex gap-4 flex-wrap tabular-nums">
          <span>Peso bruto (balança): <b className="font-mono text-[#121712]">{fmtKgBR(carga.resumo.kgBruto!)}</b></span>
          <span>Líquido: <b className="font-mono text-[#121712]">{fmtKgBR(carga.resumo.kgLiquido)}</b></span>
          {carga.resumo.diferencaBruto != null && (
            <span>Diferença: <b className="font-mono text-[#121712]">{fmtKgBR(carga.resumo.diferencaBruto)}</b></span>
          )}
        </p>
      )}

      <div className="grid grid-cols-2 gap-6 pt-6">
        <p className="border-t border-[#9ca3af] pt-1 text-[11px] text-[#4E574F]">Responsável pelo carregamento</p>
        <p className="border-t border-[#9ca3af] pt-1 text-[11px] text-[#4E574F]">
          Recebido por{carga.clienteNome ? ` (${carga.clienteNome})` : ""}
        </p>
      </div>

      <footer className="flex justify-between gap-3 flex-wrap text-[10.5px] text-[#7C857D] border-t border-[#E1E7E1] pt-2">
        <span>Documento gerado no Rizoma · rizoma.farm</span>
        <span>Valores comerciais não constam neste documento.</span>
      </footer>
    </article>
  );
}

function Campo({ rotulo, valor, mono }: { rotulo: string; valor: string; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <dt className="text-[10px] uppercase tracking-wider text-[#4E574F]">{rotulo}</dt>
      <dd className={`truncate font-semibold ${mono ? "font-mono" : ""}`}>{valor}</dd>
    </div>
  );
}

/**
 * Agrupa os itens por CONTATO — o mesmo critério do servidor.
 *
 * O mesmo produtor pode entrar duas vezes na carga (dois registros, ou um
 * romaneio e um registro), e o cliente confere procedência por pessoa, não por
 * linha de banco.
 */
function linhasPorProdutor(carga: CargaDetalheDTO) {
  const mapa = new Map<string, { contatoId: string; nome: string; porClassificacao: Record<string, number>; totalKg: number }>();
  for (const item of carga.itens) {
    const atual = mapa.get(item.produtorContatoId)
      ?? { contatoId: item.produtorContatoId, nome: item.produtorNome, porClassificacao: {}, totalKg: 0 };
    for (const [chave, kg] of Object.entries(item.porClassificacao)) {
      atual.porClassificacao[chave] = (atual.porClassificacao[chave] ?? 0) + kg;
    }
    atual.totalKg += item.kg;
    mapa.set(item.produtorContatoId, atual);
  }
  return Array.from(mapa.values());
}

// ─── Enviar ao cliente ───────────────────────────────────────────────────────

function EnviarAoCliente({ carga }: { carga: CargaDetalheDTO }) {
  const { toast } = useToast();
  const podeOperar = usePode("mercado.corretor.operar");
  const emFormacao = carga.status === "EM_FORMACAO";
  const marcar = useMarcarDocumentoEnviado(carga.id);
  // Só busca a mensagem quando ela pode ser enviada — em formação o servidor
  // responde 409, e pedi-la ali seria um erro previsível a cada abertura.
  const mensagem = useMensagemDaCarga(carga.id, !emFormacao);

  async function enviar() {
    try {
      // O evento ANTES de abrir o WhatsApp: o app não sabe se a mensagem saiu, e
      // esperar por uma confirmação que não existe deixaria o selo vazio sempre.
      await marcar.mutateAsync();
    } catch (e) {
      toast({ title: "Não consegui marcar como enviado", description: (e as Error).message, variant: "destructive" });
      return;
    }
    if (mensagem.data?.link) window.open(mensagem.data.link, "_blank", "noopener");
    else {
      await navigator.clipboard?.writeText(mensagem.data?.texto ?? "").catch(() => undefined);
      toast({ title: "Texto copiado", description: "Este cliente não tem WhatsApp cadastrado." });
    }
  }

  return (
    <section className="rounded-lg border bg-card overflow-hidden" data-testid="enviar-ao-cliente">
      <header className="px-4 py-2.5 border-b flex items-center justify-between gap-2">
        <h2 className="font-semibold text-sm">Enviar ao cliente</h2>
        {carga.documento.enviadoEm && (
          <StatusChip kind="conf">
            <Check className="h-3 w-3 mr-0.5" />Enviado {fmtDataBR(carga.documento.enviadoEm.slice(0, 10))}
          </StatusChip>
        )}
      </header>

      <div className="p-4 space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" disabled={emFormacao} asChild={!emFormacao} data-testid="button-pdf">
            {emFormacao
              ? <span><Download className="h-4 w-4 mr-1.5" />PDF</span>
              : <a href={`/api/mercado/comprador/cargas/${carga.id}/pdf`}><Download className="h-4 w-4 mr-1.5" />PDF</a>}
          </Button>
          <Button variant="outline" disabled={emFormacao} asChild={!emFormacao} data-testid="button-imagem">
            {emFormacao
              ? <span><ImageIcon className="h-4 w-4 mr-1.5" />Imagem</span>
              : <a href={`/api/mercado/comprador/cargas/${carga.id}/imagem`}><ImageIcon className="h-4 w-4 mr-1.5" />Imagem</a>}
          </Button>
        </div>

        {podeOperar && (
          <Button
            className="w-full"
            disabled={emFormacao || marcar.isPending || mensagem.isLoading}
            onClick={enviar}
            data-testid="button-enviar-whatsapp"
          >
            <MessageCircle className="h-4 w-4 mr-1.5" />
            Enviar a {carga.clienteNome ?? "o cliente"} pelo WhatsApp
          </Button>
        )}

        {emFormacao ? (
          <p className="text-xs text-muted-foreground">
            Feche a carga para liberar o envio. Enquanto está em formação, a composição ainda muda.
          </p>
        ) : mensagem.data ? (
          <pre className="whitespace-pre-wrap text-[12.5px] font-sans rounded-lg bg-muted/50 p-3 text-muted-foreground" data-testid="previa-mensagem">
            {mensagem.data.texto}
          </pre>
        ) : mensagem.isError ? (
          <p className="text-sm text-destructive">Não consegui montar a mensagem.</p>
        ) : null}
      </div>
    </section>
  );
}

// ─── O que aparece no documento ──────────────────────────────────────────────

function OQueApareceNoDocumento({ carga }: { carga: CargaDetalheDTO }) {
  const { toast } = useToast();
  const podeOperar = usePode("mercado.corretor.operar");
  const config = useConfigDocumento(carga.id);

  function alternar(campo: "mostrarProdutores" | "mostrarBruto", valor: boolean) {
    config.mutate({ [campo]: valor }, {
      // Nunca engolir: um interruptor que volta sozinho sem explicação faz o
      // corretor achar que enviou o documento com o nome dos produtores.
      onError: (e: Error) => toast({ title: "Não consegui salvar", description: e.message, variant: "destructive" }),
    });
  }

  return (
    <section className="rounded-lg border bg-card overflow-hidden" data-testid="config-documento">
      <header className="px-4 py-2.5 border-b">
        <h2 className="font-semibold text-sm">O que aparece no documento</h2>
      </header>
      <div className="p-4 space-y-3.5">
        <Interruptor
          rotulo="Nome dos produtores"
          ajuda='desligado: "Produtor 1, 2, 3…"'
          valor={carga.documento.mostrarProdutores}
          onChange={v => alternar("mostrarProdutores", v)}
          disabled={!podeOperar || config.isPending}
          testId="switch-mostrar-produtores"
        />
        <Interruptor
          rotulo="Peso bruto e diferença"
          ajuda={carga.resumo.kgBruto == null ? "aparece quando a balança do cliente informar" : undefined}
          valor={carga.documento.mostrarBruto}
          onChange={v => alternar("mostrarBruto", v)}
          disabled={!podeOperar || config.isPending}
          testId="switch-mostrar-bruto"
        />
        {/* Desabilitado e VISÍVEL de propósito: diz que a ausência de valores é
            decisão desta fase, não campo esquecido. */}
        <Interruptor
          rotulo="Preços e valores"
          ajuda="entra quando houver dados de venda"
          valor={false}
          onChange={() => undefined}
          disabled
          testId="switch-precos"
        />
      </div>
    </section>
  );
}

function Interruptor({
  rotulo, ajuda, valor, onChange, disabled, testId,
}: {
  rotulo: string;
  ajuda?: string;
  valor: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  testId: string;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="min-w-0">
        <span className="block text-sm">{rotulo}</span>
        {ajuda && <span className="block text-xs text-muted-foreground">{ajuda}</span>}
      </span>
      <Switch checked={valor} onCheckedChange={onChange} disabled={disabled} data-testid={testId} />
    </div>
  );
}
